import Dialog from "@mui/material/Dialog";
import { styled as muiStyled } from "@mui/material/styles";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { queryClient } from "../../..";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  clearSelectedDeliveryAction,
  deliveryState,
} from "../../../app/reducers/delivery-slice";
import { profileState } from "../../../app/reducers/profile-slice";
import {
  DeliveryQueryKey,
  useCreateDeliveryMutation,
  useUpdateDeliveryMutation,
} from "../../../query/delivery-query";
import theme from "../../../style/theme";
import {
  CreateActionPayload,
  UpdateActionPayload,
} from "../../../types/action/action-payload";
import { Confirm } from "../../../types/confirm";
import {
  CreateLoadPayload,
  UpdateLoadPayload,
} from "../../../types/load/load-payload";
import {
  CreateWorkPlacePayload,
  UpdateWorkPlacePayload,
} from "../../../types/work-place/work-place-payload";
import { convertISOString, dateFormat } from "../../../utils/date/date-format";
import { validateDeliveryPayload } from "../../../utils/validator/delivery";
import CloseSmallLineIcon from "../../icons/CloseSmallLineIcon/CloseSmallLineIcon";
import { DialogFooter } from "../DialogFooter";
import { BasicInfoCard } from "./BasicInfoCard";
import { DropOffActionFormCard } from "./DropOffActionFormCard";
import { PickUpActionFormCard } from "./PickUpActionFormCard";
import Asterisk from "../../asterisk/asterisk";
import {isNotDeliveryWait} from "../../../utils/delivery/delivery";

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface Props {
  setIdPayload: Dispatch<SetStateAction<string>>;
  handleClickDialogClose: () => void;
}

export interface ActionForm {
  startTimeLimited: string;
  endTimeLimited: string;
  requirement?: string;
}

export interface WorkPlaceForm {
  name?: string;
  floor?: number;
  phone?: string;
  detail: string;
}

export interface LoadForm {
  name?: string;
  quantity?: number;
}

const initPickUpActionForm: ActionForm = {
  startTimeLimited: "",
  endTimeLimited: "",
  requirement: "",
};

const initDropOffActionForm: ActionForm = {
  startTimeLimited: "",
  endTimeLimited: "",
  requirement: "",
};

const initPickUpWorkPlaceForm: WorkPlaceForm = {
  name: "",
  floor: undefined,
  phone: "",
  detail: "",
};

const initDropOffWorkPlaceForm: WorkPlaceForm = {
  name: "",
  floor: undefined,
  phone: "",
  detail: "",
};

const initLoadForm: LoadForm = {
  name: "",
  quantity: undefined,
};

const initDeliveryPayloadValidator = {
  clientId: false,
  agencyId: false,
  dateToWork: false,
  loadName: false,
  // loadQuantity: false,
  pickUpAddress: false,
  pickUpActionStartTimeLimited: false,
  pickUpActionEndTimeLimited: false,
  // pickUpActionRequirement: false,
  // pickUpWorkPlaceName: false,
  // pickUpWorkPlaceFloor: false,
  // pickUpWorkPlacePhone: false,
  pickUpWorkPlaceDetail: false,
  dropOffAddress: false,
  dropOffActionStartTimeLimited: false,
  dropOffActionEndTimeLimited: false,
  // dropOffActionRequirement: false,
  // dropOffWorkPlaceName: false,
  // dropOffWorkPlaceFloor: false,
  // dropOffWorkPlacePhone: false,
  dropOffWorkPlaceDetail: false,
};

export const DeliveryDialog: React.FC<Props> = ({ handleClickDialogClose }) => {
  const dispatch = useAppDispatch();
  const { profile } = useAppSelector(profileState);
  const { deliveryPage, selectedDelivery } = useAppSelector(deliveryState);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>("");
  const [clientsMap, setClientsMap] = useState<{ [key: string]: string }>({});
  const [agenciesMap, setAgenciesMap] = useState<{ [key: string]: string }>({});
  const [pickUpActionForm, setPickUpActionForm] =
    useState(initPickUpActionForm);
  const [pickUpAddressForm, setPickUpAddressForm] = useState("");
  const [pickUpWorkPlaceForm, setPickUpWorkPlaceForm] = useState(
    initPickUpWorkPlaceForm
  );
  const [dropOffActionForm, setDropOffActionForm] = useState(
    initDropOffActionForm
  );
  const [dropOffAddressForm, setDropOffAddressForm] = useState("");
  const [dropOffWorkPlaceForm, setDropOffWorkPlaceForm] = useState(
    initDropOffWorkPlaceForm
  );
  const [loadForm, setLoadForm] = useState(initLoadForm);
  const [dateToWork, setDateToWork] = useState<Date>(new Date());
  const [deliveryPayloadValidator, setDeliveryPayloadValidator] = useState(
    initDeliveryPayloadValidator
  );
  const createDeliveryMutaion = useCreateDeliveryMutation();
  const updateDeliveryMutaion = useUpdateDeliveryMutation();

  const handleClickCloseButton = () => {
    if (selectedDelivery) {
      dispatch(clearSelectedDeliveryAction());
    } else {
      handleClickDialogClose();
    }
  };

  const handleClickCreateDeliveryButton = async () => {
    if (!profile) {
      return;
    }

    try {
      const payload = {
        clientId: selectedClientId,
        agencyId: selectedAgencyId,
        pickUpAction: pickUpActionForm as CreateActionPayload,
        pickUpAddress: pickUpAddressForm,
        pickUpWorkPlace: arrangeCreateWorkPlacePayload(pickUpWorkPlaceForm as CreateWorkPlacePayload),
        dropOffAction: dropOffActionForm as CreateActionPayload,
        dropOffAddress: dropOffAddressForm,
        dropOffWorkPlace: arrangeCreateWorkPlacePayload(dropOffWorkPlaceForm as CreateWorkPlacePayload),
        load: loadForm as CreateLoadPayload,
        dateToWork: dateFormat(dateToWork),
      };

      if (validateDeliveryPayload(payload).hasError) {
        setDeliveryPayloadValidator(
          validateDeliveryPayload(payload).validationResults
        );
        toast.error(Confirm.Error);
        return;
      }

      createDeliveryMutaion.mutate(payload, {
        onSuccess,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const arrangeCreateWorkPlacePayload = (workPlaceForm: CreateWorkPlacePayload): CreateWorkPlacePayload => {
    return {
      ...workPlaceForm,
      floor: workPlaceForm.floor === null || workPlaceForm.floor === undefined || workPlaceForm.floor.toString() === "" ? undefined : workPlaceForm.floor,
    };
  };

  const arrangeUpdateWorkPlacePayload = (workPlaceForm: UpdateWorkPlacePayload): UpdateWorkPlacePayload => {
    return {
      ...workPlaceForm,
      floor: workPlaceForm.floor === null || workPlaceForm.floor === undefined || workPlaceForm.floor.toString() === "" ? undefined : workPlaceForm.floor,
    };
  };

  const handleClickUpdateDeliveryButton = async () => {
    if (!selectedDelivery) {
      return;
    }

    try {
      const payload = {
        clientId: selectedClientId,
        agencyId: selectedAgencyId,
        pickUpAction: pickUpActionForm as UpdateActionPayload,
        pickUpAddress: pickUpAddressForm,
        pickUpWorkPlace: arrangeUpdateWorkPlacePayload(pickUpWorkPlaceForm as UpdateWorkPlacePayload),
        dropOffAction: dropOffActionForm as UpdateActionPayload,
        dropOffAddress: dropOffAddressForm,
        dropOffWorkPlace: arrangeUpdateWorkPlacePayload(dropOffWorkPlaceForm as UpdateWorkPlacePayload),
        load: loadForm as UpdateLoadPayload,
        dateToWork: dateFormat(dateToWork),
      };

      updateDeliveryMutaion.mutate(
        { id: selectedDelivery.id, payload },
        {
          onSuccess,
        }
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickInitButton = () => {
    initSelectedDelivery();
  };

  const initSelectedDelivery = () => {
    if (selectedDelivery) {
      setSelectedClientId(selectedDelivery.client.id);
      setSelectedAgencyId(selectedDelivery.agency.id);
      setPickUpActionForm({
        startTimeLimited: convertISOString(
          selectedDelivery.pickUpAction.startTimeLimited
        ),
        endTimeLimited: convertISOString(
          selectedDelivery.pickUpAction.endTimeLimited
        ),
        requirement: selectedDelivery.pickUpAction.requirement,
      });
      setPickUpAddressForm(selectedDelivery.pickUpLocation.road);
      setPickUpWorkPlaceForm(selectedDelivery.pickUpWorkPlace);
      setDropOffActionForm({
        startTimeLimited: convertISOString(
          selectedDelivery.pickUpAction.startTimeLimited
        ),
        endTimeLimited: convertISOString(
          selectedDelivery.pickUpAction.endTimeLimited
        ),
        requirement: selectedDelivery.dropOffAction.requirement,
      });
      setDropOffAddressForm(selectedDelivery.dropOffLocation.road);
      setDropOffWorkPlaceForm(selectedDelivery.dropOffWorkPlace);
      setLoadForm(selectedDelivery.load);
      setDateToWork(new Date(selectedDelivery.dateToWork));
    } else {
      if (!profile) {
        return;
      }
      setSelectedClientId("");
      setSelectedAgencyId("");
      setPickUpActionForm(initPickUpActionForm);
      setPickUpAddressForm("");
      setPickUpWorkPlaceForm(initPickUpWorkPlaceForm);
      setDropOffActionForm(initDropOffActionForm);
      setDropOffAddressForm("");
      setDropOffWorkPlaceForm(initDropOffWorkPlaceForm);
      setLoadForm(initLoadForm);
    }
  };

  const onSuccess = () => {
    queryClient.invalidateQueries(DeliveryQueryKey.Page);
    handleClickCloseButton();
  };

  useEffect(() => {
    initSelectedDelivery();
  }, [selectedDelivery]);

  return (
    <BootstrapDialog
      onClose={handleClickCloseButton}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DeliveryFormDialogContainer>
        <TitleContainer>
          <TitleLeftContainer>
            {selectedDelivery ? "물류 수정하기" : "물류 등록하기"}</TitleLeftContainer>
          <TitleRightContianer onClick={handleClickCloseButton}>
            <CloseSmallLineIcon
              width="1.5rem"
              height="1.5rem"
              fill={theme.palette.primitives.gray100}
            />
          </TitleRightContianer>
        </TitleContainer>
        {selectedDelivery && (profile?.company.id !== selectedDelivery.agency.id) && isNotDeliveryWait(selectedDelivery) && (
            <ModifyDisabledInfoContainer>
              <Asterisk/>스케줄에 배정된 물류는 수정할 수 없습니다. 대행사에 문의하세요.
            </ModifyDisabledInfoContainer>
        )
        }
        <BodyContainer>
          <BasicInfoCard
            clientsMap={clientsMap}
            selectedClientId={selectedClientId}
            agenciesMap={agenciesMap}
            selectedAgencyId={selectedAgencyId}
            dateToWork={dateToWork}
            deliveryPayloadValidator={deliveryPayloadValidator}
            setClientsMap={setClientsMap}
            setAgenciesMap={setAgenciesMap}
            setSelectedClientId={setSelectedClientId}
            setSelectedAgencyId={setSelectedAgencyId}
            setDateToWork={setDateToWork}
          />
          <CardDivider />
          <PickUpActionFormCard
            selectedClientId={selectedClientId}
            actionForm={pickUpActionForm}
            addressForm={pickUpAddressForm}
            workPlaceForm={pickUpWorkPlaceForm}
            dateToWork={dateToWork}
            deliveryPayloadValidator={deliveryPayloadValidator}
            setActionForm={setPickUpActionForm}
            setAddressForm={setPickUpAddressForm}
            setWorkPlaceForm={setPickUpWorkPlaceForm}
          />
          <CardDivider />
          <DropOffActionFormCard
            selectedClientId={selectedClientId}
            actionForm={dropOffActionForm}
            addressForm={dropOffAddressForm}
            workPlaceForm={dropOffWorkPlaceForm}
            loadForm={loadForm}
            dateToWork={dateToWork}
            deliveryPayloadValidator={deliveryPayloadValidator}
            setActionForm={setDropOffActionForm}
            setAddressForm={setDropOffAddressForm}
            setWorkPlaceForm={setDropOffWorkPlaceForm}
            setLoadForm={setLoadForm}
          />
        </BodyContainer>
        <DialogFooter
          confirmButtonText={selectedDelivery ? "수정완료" : "등록하기"}
          disabled={
            createDeliveryMutaion.isLoading || updateDeliveryMutaion.isLoading || (selectedDelivery && profile?.company.id !== selectedDelivery?.agency.id && isNotDeliveryWait(selectedDelivery))
          }
          onClickInitButton={handleClickInitButton}
          onClickConfirmButton={
            selectedDelivery
              ? handleClickUpdateDeliveryButton
              : handleClickCreateDeliveryButton
          }
        />
      </DeliveryFormDialogContainer>
    </BootstrapDialog>
  );
};

const DeliveryFormDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 37.375rem;
  border-radius: 0.5rem;
`;

const TitleContainer = styled.div`
  height: 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${theme.palette.primitives.black};
`;

const TitleLeftContainer = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray50};
  margin-left: 1.5rem;
`;

const TitleRightContianer = styled.div`
  margin-right: 0.438rem;
  cursor: pointer;
`;

const BodyContainer = styled.div`
  height: 65vh;
  overflow-y: auto;
  padding: 2rem;
`;

const CardDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.palette.primitives.gray200};
  margin: 2rem 0 1.25rem 0;
`;

const ModifyDisabledInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 8px 2rem 8px 2rem;
  gap: 4px;
  font-style: normal;
  font-weight: 600;
  font-size: 0.813rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.red600};
  background: ${theme.palette.primitives.red50};
`

